import React from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';

const APP_URL = '/login';

const PACKAGES = {
  'llc': {
    formation: { name: 'LLC Formation', price: 199 },
    registeredAgent: { name: 'Registered Agent (1 year)', price: 150 },
    ein: { name: 'EIN Registration', price: 99 },
    operatingAgreement: { name: 'Operating Agreement', price: 0, included: true },
  },
  'c-corp': {
    formation: { name: 'C-Corp Formation', price: 299 },
    registeredAgent: { name: 'Registered Agent (1 year)', price: 150 },
    ein: { name: 'EIN Registration', price: 99 },
    bylaws: { name: 'Corporate Bylaws', price: 0, included: true },
  },
  'partnership': {
    formation: { name: 'Partnership Formation', price: 249 },
    registeredAgent: { name: 'Registered Agent (1 year)', price: 150 },
    ein: { name: 'EIN Registration', price: 99 },
  },
  'general': {
    formation: { name: 'Business Formation', price: 199 },
    registeredAgent: { name: 'Registered Agent (1 year)', price: 150 },
    ein: { name: 'EIN Registration', price: 99 },
  }
};

const FEATURES = [
  'Name availability check',
  'State filing preparation',
  'Registered agent service',
  'Operating Agreement / Bylaws',
  'EIN registration',
  'Compliance calendar',
  'Document storage',
  'Expert support',
];

const FeesSection = ({ entityType = 'llc', stateData = null, stateName = null, serviceSlug = null }) => {
  const packages = PACKAGES[entityType] || PACKAGES['general'];
  const stateFee = stateData ? (entityType === 'c-corp' ? stateData.govt_fee_ccorp : stateData.govt_fee_llc) : null;
  
  const items = Object.values(packages);
  const serviceTotal = items.reduce((sum, item) => sum + item.price, 0);
  const totalWithState = stateFee ? serviceTotal + stateFee : serviceTotal;

  const isStandalone = ['ein', 'registered-agent', 'mail-forwarding', 'boi'].includes(serviceSlug);

  if (isStandalone) {
    return null;
  }

  return (
    <section className="section-padding bg-[#070d0b]" data-testid="fees-section">
      <div className="container-custom">
        <div className="text-center mb-12">
          <span className="text-emerald-600 font-semibold text-sm uppercase tracking-widest">Pricing</span>
          <h2 className="text-3xl md:text-4xl text-white mt-3 mb-4">
            {stateName ? `${stateName} Formation` : 'Formation'}<br />
            <span className="text-slate-400">Package</span>
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Everything you need to start your business, bundled together.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-[#111c17] border border-white/10 overflow-hidden">
            {/* Header */}
            <div className="bg-[#1e293b] px-8 py-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-semibold text-white">Complete Formation Package</h3>
                  <p className="text-slate-400 text-sm mt-1">
                    {entityType === 'llc' ? 'LLC' : entityType === 'c-corp' ? 'C-Corporation' : 'Business'} Formation Bundle
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-white">${serviceTotal}</p>
                  <p className="text-slate-400 text-sm">+ state fee</p>
                </div>
              </div>
            </div>

            {/* Package Items */}
            <div className="p-8">
              <div className="space-y-4 mb-8">
                {items.map((item, index) => (
                  <div key={index} className="flex items-center justify-between py-3 border-b border-white/10 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-emerald-50 flex items-center justify-center">
                        <Check className="w-4 h-4 text-emerald-600" />
                      </div>
                      <span className="font-medium text-white">{item.name}</span>
                      {item.included && <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-1">Included</span>}
                    </div>
                    <span className="font-semibold text-white">
                      {item.included ? 'Free' : `$${item.price}`}
                    </span>
                  </div>
                ))}
                
                {stateFee && (
                  <div className="flex items-center justify-between py-3 border-b border-white/10">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-[#111c17] flex items-center justify-center">
                        <Check className="w-4 h-4 text-slate-400" />
                      </div>
                      <span className="font-medium text-white">{stateName} State Fee</span>
                    </div>
                    <span className="font-semibold text-white">${stateFee}</span>
                  </div>
                )}
              </div>

              {/* Total */}
              <div className="bg-[#070d0b] p-6 mb-8">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-semibold text-white">Total</span>
                  <div className="text-right">
                    <span className="text-3xl font-bold text-white">${totalWithState}</span>
                    {!stateFee && <p className="text-sm text-slate-400">+ state filing fee</p>}
                  </div>
                </div>
              </div>

              {/* CTA */}
              <a href={APP_URL} className="block text-center bg-[#1e293b] text-white font-semibold py-4 hover:bg-slate-800 transition-colors" data-testid="fees-cta">
                Get Started Now
                <ArrowRight className="w-5 h-5 ml-2 inline" />
              </a>
            </div>
          </div>

          {/* Features List */}
          <div className="mt-10">
            <p className="text-center text-slate-400 text-sm mb-6">Everything included in your package:</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {FEATURES.map((feature, index) => (
                <div key={index} className="flex items-center gap-2 text-sm text-slate-400">
                  <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeesSection;
